import React, { useEffect, useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import { get } from 'lodash'
import Fuse from 'fuse.js'
import { Search } from 'lucide-react'

import LegacyCard from '../leevo_ui/Card/Card'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import StudentDrawer from '../leevo_ui/StudentDrawer/StudentDrawer'
import Avatar from '../leevo_ui/Avatar/Avatar'
import toast from '../../utilities/toast'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import {
  ScrollableTableContainer,
  ScrollableTable,
  ScrollableTableHeader,
  ScrollableTableBody,
  ScrollableTableHead,
  ScrollableTableRow,
  ScrollableTableCell
} from '../ui/scrollable-table'
import { requestCreator } from '../../utilities/requests'
import { alphabetizeByFullName, readableSkateType } from '../../utilities/helpers'
import useFacility from '../../utilities/hooks/useFacility'
import useAuth from '../../utilities/hooks/useAuth'

const fuzzySearchOptions = {
  findAllMatches: true,
  keys: ['student.fullName', 'student.preferredName', 'size', 'skateType']
}

const { get: getSkateRentals, cancel: cancelSkateRentalsRequest } = requestCreator()

function FacilitiesSkateRentalsIndex() {
  const { facility, isLoading: isFacilityLoading } = useFacility()
  const facilityId = facility?.id
  const { replace: redirect } = useHistory()
  const [query, setQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [skateRentals, setSkateRentals] = useState([])
  const { isSignedIn } = useAuth()
  const [selectedSkateType, setSelectedSkateType] = useState(null)
  const [klassPackageOptions, setKlassPackageOptions] = useState([])
  const [selectedKlassPackage, setSelectedKlassPackage] = useState('all')
  const [selectedStudentId, setSelectedStudentId] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  useEffect(() => {
    if (isFacilityLoading || !facilityId) return

    setIsLoading(true)
    getSkateRentals(`/api/facilities/${facilityId}/skate_rentals`, {
      params: {
        filter: { active_eq: true },
        include: 'student',
        fields: {
          student: 'fullName,cloudinaryPhotoPublicId,klass_packages'
        }
      }
    })
      .then(({ records: skateRentals, meta }) => {
        setSkateRentals(skateRentals.sort((a, b) => alphabetizeByFullName(a.student, b.student)))
        setKlassPackageOptions([
          { label: 'All Class Packages', value: 'all' },
          ...meta.klass_packages.map(({ id, title }) => ({
            label: title,
            value: String(id)
          }))
        ])
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          if (get(error, 'response.status') === 401 && !isSignedIn) {
            redirect('/login')
          } else {
            redirect('/500')
            throw error
          }
        }
      })
      .finally(() => {
        setIsLoading(false)
      })

    return cancelSkateRentalsRequest
  }, [facilityId, isSignedIn, isFacilityLoading, redirect])

  const filteredSkateRentals = useMemo(() => {
    let filtered = skateRentals
    if (selectedSkateType !== null) {
      filtered = filtered.filter((rental) => selectedSkateType === rental.skateType)
    }
    if (selectedKlassPackage !== 'all') {
      filtered = filtered.filter((rental) =>
        rental.student.klassPackages.map(({ id }) => String(id)).includes(selectedKlassPackage)
      )
    }

    if (!query) return filtered

    const fuzzySearch = new Fuse(filtered, fuzzySearchOptions)
    return fuzzySearch.search(query).map(({ item }) => item)
  }, [selectedSkateType, selectedKlassPackage, query, skateRentals])

  const groupedSkateRentals = useMemo(() => {
    return skateRentals.reduce((accumulator, { skateType }) => {
      return {
        ...accumulator,
        [skateType]: (accumulator[skateType] || 0) + 1
      }
    }, {})
  }, [skateRentals])

  function handleRowClick(studentId) {
    setSelectedStudentId(studentId)
    setIsDrawerOpen(true)
  }

  function handleDrawerClose() {
    setIsDrawerOpen(false)
    setSelectedStudentId(null)
  }

  function handleStudentUpdate() {
    if (!facilityId) return

    setIsLoading(true)
    getSkateRentals(`/api/facilities/${facilityId}/skate_rentals`, {
      params: {
        filter: { active_eq: true },
        include: 'student',
        fields: {
          student: 'fullName,cloudinaryPhotoPublicId,klass_packages'
        }
      }
    })
      .then(({ records: skateRentals, meta }) => {
        setSkateRentals(skateRentals.sort((a, b) => alphabetizeByFullName(a.student, b.student)))
        setKlassPackageOptions([
          { label: 'All Class Packages', value: 'all' },
          ...meta.klass_packages.map(({ id, title }) => ({
            label: title,
            value: String(id)
          }))
        ])
      })
      .finally(() => setIsLoading(false))
    toast.success('Student updated successfully')
  }

  function getSkateTypeBadgeColor(skateType) {
    const colors = {
      men_figure: 'text-blue-700 border-blue-300 bg-blue-50',
      women_figure: 'text-red-700 border-red-300 bg-red-50',
      youth_figure: 'text-purple-700 border-purple-300 bg-purple-50',
      men_hockey: 'text-gray-700 border-gray-300 bg-gray-50',
      women_hockey: 'text-green-700 border-green-300 bg-green-50',
      youth_hockey: 'text-orange-700 border-orange-300 bg-orange-50'
    }
    return colors[skateType] || 'text-gray-700 border-gray-300 bg-gray-50'
  }

  return (
    <ApplicationLayout data-cy="skate-rentals-index">
      <StudentDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        studentId={selectedStudentId}
        facilityId={facility?.id}
        onUpdate={handleStudentUpdate}
      />

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 pt-6">
          <h1 className="text-2xl font-semibold text-gray-900">Skate Rentals</h1>
        </div>

        {isLoading ? (
          <LoadingSpinner size="1/5" className="flex items-center justify-center m-10" />
        ) : (
          <>
            <div className="mb-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Filter by class package:</span>
                  <Select value={selectedKlassPackage} onValueChange={setSelectedKlassPackage}>
                    <SelectTrigger className="w-full md:w-[300px]">
                      <SelectValue placeholder="Select class package" />
                    </SelectTrigger>
                    <SelectContent>
                      {klassPackageOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              {Object.keys(groupedSkateRentals)
                .sort((a, b) => a.localeCompare(b))
                .map((skateTypeKey) => (
                  <LegacyCard
                    key={skateTypeKey}
                    interactive
                    active={selectedSkateType === skateTypeKey}
                    onClick={() => setSelectedSkateType(selectedSkateType === skateTypeKey ? null : skateTypeKey)}
                    borderColor={readableSkateType(skateTypeKey).color}
                    className="p-3 text-right"
                    data-cy={`${skateTypeKey}-card`}
                  >
                    <h2 className="font-semibold text-gray-900 text-sm">
                      {readableSkateType(skateTypeKey).label}
                    </h2>
                    <span className="block text-xl font-medium text-gray-900">
                      {groupedSkateRentals[skateTypeKey]}
                    </span>
                  </LegacyCard>
                ))}
            </div>

            <div className="mb-6">
              <div className="relative max-w-xl">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="search"
                  type="text"
                  className="block w-full pl-10 pr-12 py-3 text-base text-gray-900 placeholder-gray-500 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent shadow-sm transition-all h-12"
                  placeholder="Search by name, type, or size..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {(query || selectedSkateType || selectedKlassPackage !== 'all') && (
                <p className="mt-2 text-sm text-gray-600">
                  {filteredSkateRentals.length} {filteredSkateRentals.length === 1 ? 'result' : 'results'} found
                </p>
              )}
            </div>

            <ScrollableTableContainer className="hidden lg:block mb-6" height={650}>
              <ScrollableTable>
                <ScrollableTableHeader>
                  <ScrollableTableRow>
                    <ScrollableTableHead>Student</ScrollableTableHead>
                    <ScrollableTableHead className="text-center">Skate Type</ScrollableTableHead>
                    <ScrollableTableHead className="text-center">Size</ScrollableTableHead>
                    <ScrollableTableHead className="text-right">Actions</ScrollableTableHead>
                  </ScrollableTableRow>
                </ScrollableTableHeader>
                <ScrollableTableBody>
                  {filteredSkateRentals.map((rental) => (
                    <ScrollableTableRow
                      key={rental.id}
                      onClick={() => handleRowClick(rental.student.id)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <ScrollableTableCell>
                        <div className="flex items-center gap-3">
                          <Avatar
                            fullName={rental.student.fullName}
                            cloudinaryPhotoPublicId={rental.student.cloudinaryPhotoPublicId}
                            userId={rental.student.id}
                            isStudent
                            size={10}
                          />
                          <p className="font-semibold text-gray-900">{rental.student.fullName}</p>
                        </div>
                      </ScrollableTableCell>
                      <ScrollableTableCell className="text-center">
                        <Badge variant="outline" className={getSkateTypeBadgeColor(rental.skateType)}>
                          {readableSkateType(rental.skateType)?.label}
                        </Badge>
                      </ScrollableTableCell>
                      <ScrollableTableCell className="text-center">
                        <span className="text-gray-700 font-medium">{rental.size}</span>
                      </ScrollableTableCell>
                      <ScrollableTableCell>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRowClick(rental.student.id)
                            }}
                            className="h-8"
                          >
                            Edit
                          </Button>
                        </div>
                      </ScrollableTableCell>
                    </ScrollableTableRow>
                  ))}
                  {filteredSkateRentals.length === 0 && (
                    <ScrollableTableRow>
                      <ScrollableTableCell colSpan={4} className="text-center py-8 text-gray-500">
                        {query || selectedSkateType || selectedKlassPackage !== 'all'
                          ? 'No skate rentals found matching your filters.'
                          : 'No active skate rentals.'}
                      </ScrollableTableCell>
                    </ScrollableTableRow>
                  )}
                </ScrollableTableBody>
              </ScrollableTable>
            </ScrollableTableContainer>

            <div className="space-y-3 lg:hidden">
              {filteredSkateRentals.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500 bg-white border border-gray-200 rounded-lg">
                  {query || selectedSkateType || selectedKlassPackage !== 'all'
                    ? 'No skate rentals found matching your filters.'
                    : 'No active skate rentals.'}
                </div>
              ) : (
                filteredSkateRentals.map((rental) => (
                  <div
                    key={rental.id}
                    onClick={() => handleRowClick(rental.student.id)}
                    className="p-4 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar
                        fullName={rental.student.fullName}
                        cloudinaryPhotoPublicId={rental.student.cloudinaryPhotoPublicId}
                        userId={rental.student.id}
                        isStudent
                        size={12}
                      />
                      <p className="font-semibold text-gray-900 flex-1">{rental.student.fullName}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Skate Type</label>
                        <Badge variant="outline" className={getSkateTypeBadgeColor(rental.skateType)}>
                          {readableSkateType(rental.skateType)?.label}
                        </Badge>
                      </div>
                      <div>
                        <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Size</label>
                        <p className="text-sm font-medium text-gray-900">{rental.size}</p>
                      </div>
                    </div>

                    <div className="pt-3 mt-3 border-t border-gray-200">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRowClick(rental.student.id)
                        }}
                        className="w-full h-9"
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </ApplicationLayout>
  )
}

export default FacilitiesSkateRentalsIndex
